/**
 * ManagePlans.js
 *
 * FEATURE (this version) — plan-based feature gating UI.
 *   Each plan can now have an `included_features` list — which nav
 *   items/pages are available to companies on this plan. Rendered as
 *   grouped checkboxes (mirroring DashboardLayout.js's section
 *   structure via the shared navFeatures.js reference list) inside the
 *   create/edit modal.
 *
 *   IMPORTANT: a plan that has NEVER had this configured (no checkboxes
 *   ever saved) has included_features = null on the backend, which
 *   DashboardLayout.js treats as "everything included" — fully
 *   unrestricted. This means EXISTING plans are completely unaffected
 *   until a SuperAdmin actually opens this editor and saves a feature
 *   selection. The UI defaults to "all features checked" for a plan
 *   that has no included_features yet, so opening the editor doesn't
 *   visually suggest anything is currently restricted when it isn't.
 *
 *   Run once before deploying (backend):
 *     ALTER TABLE subscription_plans ADD COLUMN included_features JSON NULL;
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, Modal, Form, Spinner, Table, Accordion } from 'react-bootstrap';
import { Plus, Edit2, Trash2, Check, IndianRupee, Layers } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import NAV_FEATURES from '../../constants/navFeatures';

const ALL_FEATURE_PATHS = NAV_FEATURES.map(f => f.path);
const FEATURE_SECTIONS = [...new Set(NAV_FEATURES.map(f => f.section))];

const defaultPlan = {
    name:'', plan_code:'', monthly_price:'', yearly_price:'', max_employees:50,
    currency:'INR', features:[''], is_active:true,
    included_features: null, // null = unrestricted (all features) — see header comment
};

const ManagePlans = () => {
    const [plans,   setPlans]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing,   setEditing]   = useState(null);
    const [form,      setForm]      = useState(defaultPlan);
    const [saving,    setSaving]    = useState(false);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/superadmin/plans');
            setPlans(res.data.data || []);
        } catch { toast.error('Failed to load plans'); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchPlans(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(defaultPlan);
        setShowModal(true);
    };
    const openEdit = (plan) => {
        setEditing(plan);
        const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
        // FEATURE: parse included_features the same defensive way as
        // the backend — if it's a JSON string, parse it; if null/absent,
        // keep it null (meaning "unrestricted").
        let includedFeatures = null;
        if (plan.included_features != null) {
            try {
                includedFeatures = typeof plan.included_features === 'string'
                    ? JSON.parse(plan.included_features)
                    : plan.included_features;
            } catch { includedFeatures = null; }
        }
        setForm({ ...plan, features: features.length ? features : [''], included_features: includedFeatures });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name || !form.monthly_price) { toast.warn('Name and monthly price are required'); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                features: form.features.filter(f => f.trim()),
                monthly_price: parseFloat(form.monthly_price),
                yearly_price: parseFloat(form.yearly_price || form.monthly_price * 10),
                // FEATURE: included_features is sent as-is — either null
                // (unrestricted) or an array of path strings the
                // SuperAdmin has checked.
                included_features: form.included_features,
            };
            if (editing) {
                await axiosInstance.put(`/superadmin/plans/${editing.id}`, payload);
                toast.success('Plan updated');
            } else {
                await axiosInstance.post('/superadmin/plans', payload);
                toast.success('Plan created');
            }
            setShowModal(false);
            fetchPlans();
        } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
        finally { setSaving(false); }
    };
    const handleDelete = async (id, name) => {
        if (!window.confirm(`Deactivate plan "${name}"? Existing subscribers will not be affected.`)) return;
        try {
            await axiosInstance.delete(`/superadmin/plans/${id}`);
            toast.success('Plan deactivated');
            fetchPlans();
        } catch { toast.error('Failed'); }
    };
    const updateFeature = (idx, val) => {
        const f = [...form.features];
        f[idx] = val;
        setForm(p => ({ ...p, features: f }));
    };
    const addFeature    = () => setForm(p => ({ ...p, features: [...p.features, ''] }));
    const removeFeature = (idx) => setForm(p => ({ ...p, features: p.features.filter((_, i) => i !== idx) }));
    const fmt = (n) => parseFloat(n || 0).toLocaleString('en-IN');

    // FEATURE: feature-checklist helpers. `form.included_features` is
    // either null (unrestricted — every checkbox should render as
    // checked, since nothing is actually excluded) or an array of
    // path strings (only those are checked).
    const isFeatureChecked = (path) =>
        form.included_features === null ? true : form.included_features.includes(path);

    const toggleFeature = (path) => {
        setForm(p => {
            // Lazily convert from "unrestricted" to a real array the
            // moment the SuperAdmin first unchecks something — starting
            // from "everything currently included" rather than empty,
            // so unchecking ONE box doesn't accidentally hide everything else.
            const current = p.included_features === null ? [...ALL_FEATURE_PATHS] : [...p.included_features];
            const next = current.includes(path)
                ? current.filter(x => x !== path)
                : [...current, path];
            return { ...p, included_features: next };
        });
    };
    const selectAllInSection = (section) => {
        const sectionPaths = NAV_FEATURES.filter(f => f.section === section).map(f => f.path);
        setForm(p => {
            const current = p.included_features === null ? [...ALL_FEATURE_PATHS] : [...p.included_features];
            const merged = [...new Set([...current, ...sectionPaths])];
            return { ...p, included_features: merged };
        });
    };
    const clearAllInSection = (section) => {
        const sectionPaths = NAV_FEATURES.filter(f => f.section === section).map(f => f.path);
        setForm(p => {
            const current = p.included_features === null ? [...ALL_FEATURE_PATHS] : [...p.included_features];
            const cleared = current.filter(x => !sectionPaths.includes(x));
            return { ...p, included_features: cleared };
        });
    };
    const resetToUnrestricted = () => setForm(p => ({ ...p, included_features: null }));

    const includedCount = form.included_features === null
        ? ALL_FEATURE_PATHS.length
        : form.included_features.length;

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">Pricing Plans</h4>
                    <p className="text-muted small mb-0">Manage subscription plans shown to new customers</p>
                </div>
                <Button variant="primary" className="d-flex align-items-center gap-2" onClick={openCreate}>
                    <Plus size={18} />New Plan
                </Button>
            </div>
            {loading ? <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div> : (
                <Row className="g-3">
                    {plans.map(plan => {
                        const features = typeof plan.features === 'string' ? JSON.parse(plan.features || '[]') : (plan.features || []);
                        let featCount = null;
                        if (plan.included_features != null) {
                            try {
                                const parsed = typeof plan.included_features === 'string'
                                    ? JSON.parse(plan.included_features) : plan.included_features;
                                featCount = Array.isArray(parsed) ? parsed.length : null;
                            } catch { featCount = null; }
                        }
                        return (
                            <Col key={plan.id} xs={12} sm={6} lg={4}>
                                <Card className="border-0 shadow-sm h-100" style={{ opacity: plan.is_active ? 1 : 0.55 }}>
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h5 className="fw-bold mb-0">{plan.name}</h5>
                                                <code className="text-muted" style={{ fontSize:11 }}>{plan.plan_code}</code>
                                            </div>
                                            <Badge bg={plan.is_active ? 'success' : 'secondary'}>{plan.is_active ? 'Active' : 'Inactive'}</Badge>
                                        </div>
                                        <div className="mb-3">
                                            <div className="d-flex align-items-baseline gap-1">
                                                <span style={{ fontSize:28, fontWeight:700, color:'#1e293b' }}>₹{fmt(plan.monthly_price)}</span>
                                                <span className="text-muted small">/month</span>
                                            </div>
                                            <div className="text-muted small">₹{fmt(plan.yearly_price)}/year · {plan.max_employees} employees</div>
                                        </div>
                                        {/* FEATURE: shows whether this plan has nav-gating configured */}
                                        <div className="mb-3">
                                            {featCount === null ? (
                                                <Badge bg="light" text="dark" className="d-inline-flex align-items-center gap-1">
                                                    <Layers size={11}/> All features included
                                                </Badge>
                                            ) : (
                                                <Badge bg="info" text="dark" className="d-inline-flex align-items-center gap-1">
                                                    <Layers size={11}/> {featCount} of {ALL_FEATURE_PATHS.length} features
                                                </Badge>
                                            )}
                                        </div>
                                        <ul className="list-unstyled mb-4">
                                            {features.map((f, i) => (
                                                <li key={i} className="d-flex align-items-center gap-2 mb-2 small">
                                                    <Check size={14} className="text-success flex-shrink-0" />{f}
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="d-flex gap-2 mt-auto">
                                            <Button variant="outline-primary" size="sm" className="flex-grow-1" onClick={() => openEdit(plan)}>
                                                <Edit2 size={14} className="me-1" />Edit
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(plan.id, plan.name)}>
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
            {/* Plan table summary */}
            {plans.length > 0 && (
                <Card className="border-0 shadow-sm mt-4">
                    <Card.Header className="bg-white border-0 py-3 fw-bold">Plan Comparison Table</Card.Header>
                    <Table responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                                <th className="ps-4">Plan</th><th>Code</th><th>Monthly</th><th>Yearly</th><th>Max Employees</th><th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map(p => (
                                <tr key={p.id}>
                                    <td className="ps-4 fw-medium">{p.name}</td>
                                    <td><code>{p.plan_code}</code></td>
                                    <td>₹{fmt(p.monthly_price)}</td>
                                    <td>₹{fmt(p.yearly_price)}</td>
                                    <td>{p.max_employees}</td>
                                    <td><Badge bg={p.is_active ? 'success' : 'secondary'}>{p.is_active ? 'Active' : 'Inactive'}</Badge></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            {/* Create / Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" scrollable>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">{editing ? `Edit — ${editing.name}` : 'Create New Plan'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Plan Name *</Form.Label>
                            <Form.Control placeholder="e.g. Growth" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Plan Code</Form.Label>
                            <Form.Control placeholder="e.g. growth" value={form.plan_code} onChange={e => setForm(p => ({ ...p, plan_code: e.target.value.toLowerCase().replace(/\s+/g,'-') }))} />
                        </Col>
                        <Col xs={12} sm={4}>
                            <Form.Label className="small fw-bold">Monthly Price (₹) *</Form.Label>
                            <Form.Control type="number" min="0" placeholder="2499" value={form.monthly_price} onChange={e => setForm(p => ({ ...p, monthly_price: e.target.value }))} />
                        </Col>
                        <Col xs={12} sm={4}>
                            <Form.Label className="small fw-bold">Yearly Price (₹)</Form.Label>
                            <Form.Control type="number" min="0" placeholder="Auto (×10)" value={form.yearly_price} onChange={e => setForm(p => ({ ...p, yearly_price: e.target.value }))} />
                        </Col>
                        <Col xs={12} sm={4}>
                            <Form.Label className="small fw-bold">Max Employees</Form.Label>
                            <Form.Control type="number" min="1" value={form.max_employees} onChange={e => setForm(p => ({ ...p, max_employees: parseInt(e.target.value) }))} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Currency</Form.Label>
                            <Form.Select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>
                                <option value="INR">INR — Indian Rupee</option>
                                <option value="USD">USD — US Dollar</option>
                                <option value="GBP">GBP — British Pound</option>
                                <option value="AED">AED — UAE Dirham</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Features / Highlights</Form.Label>
                            {form.features.map((f, i) => (
                                <div key={i} className="d-flex gap-2 mb-2">
                                    <Form.Control size="sm" placeholder={`Feature ${i+1}`} value={f} onChange={e => updateFeature(i, e.target.value)} />
                                    <Button variant="outline-danger" size="sm" onClick={() => removeFeature(i)} disabled={form.features.length === 1}>×</Button>
                                </div>
                            ))}
                            <Button variant="outline-secondary" size="sm" onClick={addFeature}><Plus size={14} className="me-1" />Add Feature</Button>
                        </Col>
                        {editing && (
                            <Col xs={12}>
                                <Form.Check type="switch" id="plan-active" label="Plan is active (visible to new customers)" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                            </Col>
                        )}
                        {/* FEATURE: nav feature checklist, grouped by section */}
                        <Col xs={12}>
                            <hr/>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="small fw-bold mb-0 d-flex align-items-center gap-2">
                                    <Layers size={14}/> App Features Included in This Plan
                                </Form.Label>
                                <Badge bg={form.included_features === null ? 'light' : 'info'} text="dark">
                                    {includedCount} of {ALL_FEATURE_PATHS.length} included
                                </Badge>
                            </div>
                            <p className="text-muted small mb-3">
                                Controls which sidebar pages are visible to companies on this plan.
                                {form.included_features === null && (
                                    <strong> Currently unrestricted — every feature is available.</strong>
                                )}
                            </p>
                            {form.included_features !== null && (
                                <Button variant="outline-secondary" size="sm" className="mb-3"
                                    onClick={resetToUnrestricted}>
                                    Reset to "All Features Included"
                                </Button>
                            )}
                            <Accordion alwaysOpen={false}>
                                {FEATURE_SECTIONS.map((section, idx) => {
                                    const sectionFeatures = NAV_FEATURES.filter(f => f.section === section);
                                    const checkedInSection = sectionFeatures.filter(f => isFeatureChecked(f.path)).length;
                                    return (
                                        <Accordion.Item eventKey={String(idx)} key={section}>
                                            <Accordion.Header>
                                                <span className="d-flex justify-content-between w-100 me-3">
                                                    <span>{section}</span>
                                                    <Badge bg="light" text="dark">{checkedInSection}/{sectionFeatures.length}</Badge>
                                                </span>
                                            </Accordion.Header>
                                            <Accordion.Body className="pt-2">
                                                <div className="d-flex gap-2 mb-3">
                                                    <Button variant="link" size="sm" className="p-0"
                                                        onClick={() => selectAllInSection(section)}>
                                                        Select all
                                                    </Button>
                                                    <span className="text-muted">·</span>
                                                    <Button variant="link" size="sm" className="p-0 text-danger"
                                                        onClick={() => clearAllInSection(section)}>
                                                        Clear all
                                                    </Button>
                                                </div>
                                                <Row className="g-2">
                                                    {sectionFeatures.map(f => (
                                                        <Col xs={12} sm={6} key={f.path}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`feat-${f.path}`}
                                                                label={f.name}
                                                                checked={isFeatureChecked(f.path)}
                                                                onChange={() => toggleFeature(f.path)}
                                                            />
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? <Spinner size="sm" /> : editing ? 'Save Changes' : 'Create Plan'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
export default ManagePlans;
